/**
 * Direct add-to-cart handler
 *
 * Two responsibilities:
 * 1. BAJO PEDIDO buttons ([data-bajopedido-trigger]) — always intercepted, everywhere.
 * 2. Quick-add buttons (.quick-add__button--add inside product-card) on the PRODUCT PAGE —
 *    intercepted because third-party apps (Planet, STOQ, restockrocket, free-gift)
 *    hijack native form submissions only on product templates.
 *
 * Both cases use capture-phase event delegation on document so they work for
 * server-rendered AND dynamically injected buttons (infinite scroll, product-tabs, etc.).
 *
 * Includes native animations: atc-added text swap + fly-to-cart bezier animation.
 */
(function () {
  if (window.__bajopedidoDirectAddInit) return;
  window.__bajopedidoDirectAddInit = true;

  // Detect product page template (Theme object is defined before defer scripts execute)
  var templateName = (typeof Theme !== 'undefined' && Theme.template) ? Theme.template.name : '';
  var isProductPage = templateName === 'product' || templateName.indexOf('product.') === 0;

  // ── Intercept /cart/change.js to sync cart icon when Planet removes/changes items ──
  // Planet makes silent AJAX calls without dispatching cart:update, so we hook fetch here.
  (function () {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
      var result = origFetch.apply(this, arguments);
      if (url.indexOf('/cart/change.js') !== -1) {
        result.then(function (response) {
          return response.clone().json();
        }).then(function (data) {
          if (data && typeof data.item_count === 'number') {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: {},
                sourceId: '',
                data: { source: 'cart-change', itemCount: data.item_count }
              }
            }));
          }
        }).catch(function () {});
      }
      return result;
    };
  })();

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // ── Case 1: Bajo pedido direct-add button ──
    var bpTrigger = target.closest('[data-bajopedido-trigger]');
    if (bpTrigger) {
      var wrapper = bpTrigger.closest('.bajopedido-direct-add');
      if (!wrapper) return;

      event.stopPropagation();
      event.preventDefault();

      var variantId = wrapper.dataset.bajopedidoVariantId;
      var productId = wrapper.dataset.bajopedidoProductId;
      if (!variantId || bpTrigger.classList.contains('direct-add--loading')) return;

      // Find product image for fly-to-cart
      var productCard = bpTrigger.closest('product-card');
      var productImage = productCard ? productCard.querySelector('img') : null;
      var imageSrc = productImage ? productImage.src : null;

      directAddToCart(variantId, productId, '1', bpTrigger, imageSrc);
      return;
    }

    // ── Case 2: Quick-add button inside product-card on product page ──
    if (isProductPage) {
      var addBtn = target.closest('.quick-add__button--add');
      if (!addBtn) return;

      var card = addBtn.closest('product-card');
      if (!card) return; // Safety: don't intercept the main product form

      event.stopPropagation();
      event.preventDefault();

      // Extract variant ID and quantity from the form
      var form = addBtn.closest('form');
      if (!form) return;

      var variantInput = form.querySelector('input[name="id"]');
      var quantityInput = form.querySelector('input[name="quantity"]');
      if (!variantInput || !variantInput.value) return;

      var vid = variantInput.value;
      var qty = (quantityInput && quantityInput.value) ? quantityInput.value : '1';
      var productFormComp = addBtn.closest('product-form-component');
      var pid = productFormComp ? productFormComp.dataset.productId : '';

      if (addBtn.classList.contains('direct-add--loading')) return;

      // Get image for fly-to-cart from the add-to-cart-component or the card
      var atcComp = addBtn.closest('add-to-cart-component');
      var imgSrc = (atcComp && atcComp.dataset.productVariantMedia)
        ? atcComp.dataset.productVariantMedia
        : null;
      if (!imgSrc) {
        var cardImg = card.querySelector('img');
        imgSrc = cardImg ? (cardImg.currentSrc || cardImg.src) : null;
      }

      // Skip animation: Component.js capture handler already fired
      // AddToCartComponent.handleClick() before us, triggering atc-added + fly-to-cart
      directAddToCart(vid, pid, qty, addBtn, imgSrc, true);
      return;
    }
  }, true); // capture phase — fires before third-party bubble-phase handlers

  /**
   * Direct AJAX add-to-cart with animations
   * @param {string} variantId
   * @param {string} productId
   * @param {string} quantity
   * @param {HTMLElement} button - The button element to animate
   * @param {string|null} imageSrc - Product image for fly-to-cart (optional)
   * @param {boolean} [skipAnimation] - If true, skip atc-added + fly-to-cart (native handler already did it)
   */
  function directAddToCart(variantId, productId, quantity, button, imageSrc, skipAnimation) {
    button.classList.add('direct-add--loading');

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Collect section IDs for cart drawer update
    var cartItems = document.querySelectorAll('cart-items-component');
    var sectionIds = [];
    cartItems.forEach(function (item) {
      if (item.dataset && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    var cartAddUrl =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js';

    fetch(cartAddUrl, { method: 'POST', body: formData })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        button.classList.remove('direct-add--loading');

        if (data.status) {
          // Shopify API error
          console.error('[DirectAdd] Error:', data.message || data.description);
          button.classList.add('bajopedido-error');
          setTimeout(function () { button.classList.remove('bajopedido-error'); }, 2000);
          return;
        }

        // ── Success animations (skip if native handler already triggered them) ──
        if (!skipAnimation) {
          // 1. Text swap: "Añadir" → "Agregada ✓"
          if (!button.classList.contains('atc-added')) {
            button.classList.add('atc-added');
            setTimeout(function () {
              button.classList.remove('atc-added');
            }, 2000);
          }

          // 2. Fly-to-cart bezier animation (reuses native fly-to-cart custom element)
          animateFlyToCart(button, imageSrc);
        }

        // ── Open Planet's smart-cart drawer ──
        var pd = window.PlanetDataset;
        if (pd && pd.SmartCart && pd.SmartCart.methods && pd.SmartCart.methods.openDrawer) {
          pd.SmartCart.methods.openDrawer(true);
        }

        // ── Dispatch cart:update event for cart icon + cart drawer ──
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: String(variantId),
            data: {
              source: 'product-form-component',
              productId: productId,
              itemCount: parseInt(quantity) || 1,
              sections: data.sections || {},
            }
          }
        }));
      })
      .catch(function (err) {
        console.error('[DirectAdd] Fetch error:', err);
        button.classList.remove('direct-add--loading');
      });
  }

  /**
   * Trigger the native fly-to-cart bezier animation
   * The <fly-to-cart> custom element is defined in product-form.js
   * @param {HTMLElement} button
   * @param {string|null} imageSrc
   */
  function animateFlyToCart(button, imageSrc) {
    if (!imageSrc) return;

    var cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon) return;

    // Check that the fly-to-cart custom element is registered
    if (!customElements.get('fly-to-cart')) return;

    var flyEl = document.createElement('fly-to-cart');
    flyEl.style.setProperty('background-image', 'url(' + imageSrc + ')');
    flyEl.source = button;
    flyEl.destination = cartIcon;
    document.body.appendChild(flyEl);
  }
})();
